import { z } from 'zod';

import {
  INTERVIEW_LOCATION_MAX_LENGTH,
  INTERVIEW_SLOT_MAX_CAPACITY,
  InterviewSlotAudienceSchema,
  InterviewSlotSchema,
  InterviewSlotStatusSchema,
} from '../interview-slot/interview-slot';
import {
  InterviewMethodSchema,
  LOCATION_REQUIRED_MESSAGE,
  requiresLocationOrLink,
  type InterviewMethod,
} from '../interview/interview';

/**
 * Bounded so one form submission cannot publish a year of every quarter hour.
 * A term's availability at one slot an hour, eight hours a day, is a few
 * hundred; the same ceiling `VENUE_SLOT_BATCH_MAX` chose.
 */
export const INTERVIEW_SLOT_BATCH_MAX = 500;

/** Trimmed to a non-empty string or explicitly absent: whitespace is not a venue. */
const LocationSchema = z.string().trim().min(1).max(INTERVIEW_LOCATION_MAX_LENGTH).nullable();

export const InterviewSlotWritableShape = {
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  audience: InterviewSlotAudienceSchema,
  method: InterviewMethodSchema,
  capacity: z.number().int().min(1).max(INTERVIEW_SLOT_MAX_CAPACITY),
  status: InterviewSlotStatusSchema,
  locationOrLink: LocationSchema,
};

const endsAfterStart = (slot: { startsAt: string; endsAt: string }): boolean =>
  Date.parse(slot.endsAt) > Date.parse(slot.startsAt);

/** A video window carries its link; the api re-asks this on the MERGED row of a patch. */
const hasRequiredLocation = (slot: {
  method?: InterviewMethod;
  locationOrLink?: string | null;
}): boolean =>
  slot.method === undefined ||
  slot.locationOrLink === undefined ||
  !requiresLocationOrLink(slot.method) ||
  slot.locationOrLink !== null;

const LOCATION_REQUIRED = { message: LOCATION_REQUIRED_MESSAGE, path: ['locationOrLink'] };

export const InterviewSlotCreateSchema = z
  .object({ ...InterviewSlotWritableShape })
  .strict()
  .refine(endsAfterStart, { message: 'A slot must end after it starts.', path: ['endsAt'] })
  .refine(hasRequiredLocation, LOCATION_REQUIRED);

export type InterviewSlotCreateRequest = z.infer<typeof InterviewSlotCreateSchema>;

/**
 * Editing one window. Every field optional; absent means "leave it". The api
 * merges the patch with the row **under its lock** before it re-checks the
 * window, so an `endsAt` alone is measurable there and not here — and the
 * link rule is only decidable here when the patch names both halves.
 */
export const InterviewSlotUpdateSchema = z
  .object({ ...InterviewSlotWritableShape })
  .partial()
  .strict()
  .refine(hasRequiredLocation, LOCATION_REQUIRED);

export type InterviewSlotUpdateRequest = z.infer<typeof InterviewSlotUpdateSchema>;

/**
 * One entry of a generated recurrence. Already an **instant** — the rule that
 * produced it (every Tuesday, 18:00–21:00, half-hour slots) is expanded on the
 * client, in the operator's zone, and never stored: a rule expanded at read
 * time drifts across a DST change, and a stored instant does not (P25b2).
 *
 * No `status`: a batch is published to be offered. The method, audience and
 * location travel on the **batch**, once, because a recurrence is one decision.
 */
export const InterviewSlotBatchEntrySchema = z
  .object({
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    capacity: z.number().int().min(1).max(INTERVIEW_SLOT_MAX_CAPACITY),
  })
  .strict()
  .refine(endsAfterStart, { message: 'A slot must end after it starts.', path: ['endsAt'] });

export type InterviewSlotBatchEntry = z.infer<typeof InterviewSlotBatchEntrySchema>;

export const InterviewSlotBatchSchema = z
  .object({
    audience: InterviewSlotAudienceSchema,
    method: InterviewMethodSchema,
    locationOrLink: LocationSchema,
    slots: z.array(InterviewSlotBatchEntrySchema).min(1).max(INTERVIEW_SLOT_BATCH_MAX),
    /**
     * `false` refuses the whole batch on the first clash and writes nothing;
     * `true` passes over the clashes and counts them — "nineteen of your
     * twenty-four" is the sentence the surface has to be able to say.
     */
    skipOverlapping: z.boolean(),
  })
  .strict()
  .refine(hasRequiredLocation, LOCATION_REQUIRED);

export type InterviewSlotBatchRequest = z.infer<typeof InterviewSlotBatchSchema>;

export const InterviewSlotBatchResultSchema = z
  .object({
    created: z.array(InterviewSlotSchema),
    skipped: z.number().int().nonnegative(),
  })
  .strict();

export type InterviewSlotBatchResult = z.infer<typeof InterviewSlotBatchResultSchema>;
